import { type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { type EditorState } from '@tiptap/pm/state';

import {
  EAiEditSuggestionKind,
  type TAiEditSuggestion,
  type TAiEditTargetGroup,
  type TPlannedAiEdit,
  type TPlannedAiEditInsertion,
  type TPlannedAiEditSuggestion,
  type TPlannedAiEditTarget,
} from './types';

type TDocumentIndex = {
  nodes: Map<string, TPlannedAiEditTarget>;
  /** id -> loadedId for atoms whose registration has not settled in the baseline. */
  unsettledAtomSources: Map<string, string>;
};

const indexDocument = (doc: PMNode): TDocumentIndex => {
  const nodes = new Map<string, TPlannedAiEditTarget>();
  const unsettledAtomSources = new Map<string, string>();
  doc.descendants((node, pos) => {
    const id = typeof node.attrs.id === 'string' ? node.attrs.id : undefined;
    if (!id) return;
    if (!nodes.has(id)) nodes.set(id, { node, pos, end: pos + node.nodeSize });
    const loadedId = typeof node.attrs.loadedId === 'string' ? node.attrs.loadedId : undefined;
    if (loadedId && loadedId !== id) unsettledAtomSources.set(id, loadedId);
  });
  return { nodes, unsettledAtomSources };
};

/**
 * The backend is the only id author now - this walk only records atom sources for the preview.
 * Atoms never nest, but they sit deep inside a suggestion (a ref inside a replaced paragraph).
 */
const collectAtomSources = (root: PMNode, atomSources: Map<string, string>): void => {
  const record = (node: PMNode): void => {
    const id = typeof node.attrs.id === 'string' ? node.attrs.id : undefined;
    if (!id) return;
    // This node now owns the id, so its own loadedId replaces any baseline seed for it.
    atomSources.delete(id);
    const loadedId = typeof node.attrs.loadedId === 'string' ? node.attrs.loadedId : undefined;
    if (loadedId && loadedId !== id) atomSources.set(id, loadedId);
  };
  record(root);
  root.descendants(record);
};

const planSuggestion = (
  suggestion: TAiEditSuggestion,
  target: TPlannedAiEditTarget | undefined,
  schema: Schema,
  atomSources: Map<string, string>
): TPlannedAiEditSuggestion => {
  if (suggestion.kind === EAiEditSuggestionKind.REMOVE) return { ...suggestion, target };

  const node = schema.nodeFromJSON(suggestion.node);
  collectAtomSources(node, atomSources);
  return { ...suggestion, target, node };
};

export const planAiEdit = (suggestions: TAiEditSuggestion[], state: EditorState): TPlannedAiEdit => {
  const { nodes, unsettledAtomSources } = indexDocument(state.doc);
  const previewAtomSources = new Map(unsettledAtomSources);
  const plannedSuggestions = suggestions.map((suggestion) =>
    planSuggestion(suggestion, nodes.get(suggestion.elementId), state.schema, previewAtomSources)
  );

  return { baselineDoc: state.doc, suggestions: plannedSuggestions, previewAtomSources };
};

type TInsertionSides = { before: TPlannedAiEditInsertion[]; after: TPlannedAiEditInsertion[] };

const isInsertion = (suggestion: TPlannedAiEditSuggestion): suggestion is TPlannedAiEditInsertion =>
  suggestion.kind === EAiEditSuggestionKind.INSERT_BEFORE || suggestion.kind === EAiEditSuggestionKind.INSERT_AFTER;

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/ai/useCases/edit/src/validation/plan/incrementalEditPlan.ts
/**
 * The one ordering rule: around any anchor, saved or inserted, its insertions keep list order, and an
 * inserted anchor expands in place to [its befores, itself, its afters].
 */
export const groupAiEditSuggestions = (suggestions: readonly TPlannedAiEditSuggestion[]): TAiEditTargetGroup[] => {
  const groups = new Map<string, TAiEditTargetGroup>();
  const insertedSides = new Map<string, TInsertionSides>();
  const place = (sides: TInsertionSides, insertion: TPlannedAiEditInsertion): void => {
    (insertion.kind === EAiEditSuggestionKind.INSERT_BEFORE ? sides.before : sides.after).push(insertion);
    insertedSides.set(insertion.node.attrs.id, { before: [], after: [] });
  };

  suggestions.forEach((suggestion) => {
    const { elementId, target } = suggestion;
    if (target) {
      const group = groups.get(elementId) ?? { elementId, target, before: [], destructive: [], after: [] };
      groups.set(elementId, group);
      if (isInsertion(suggestion)) place(group, suggestion);
      else group.destructive.push(suggestion);
      return;
    }
    const sides = insertedSides.get(elementId);
    if (!sides || !isInsertion(suggestion)) throw new Error('MISSING_SUGGESTION_TARGET');
    place(sides, suggestion);
  });

  const expand = (insertions: TPlannedAiEditInsertion[]): TPlannedAiEditInsertion[] =>
    insertions.flatMap((insertion) => {
      const sides = insertedSides.get(insertion.node.attrs.id);
      return sides ? [...expand(sides.before), insertion, ...expand(sides.after)] : [insertion];
    });
  return [...groups.values()].map((group) => ({ ...group, before: expand(group.before), after: expand(group.after) }));
};
